import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { taskApi } from '../../../../entities/task/api/service'
import { taskKeys } from '../../../../entities/task/api/task.keys'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DurationSpinner } from '../../../../shared/ui/controls/DurationSpinner/DurationSpinner'
import Modal from '../../../../shared/ui/overlay/Modal/Modal'
import { departureReminderFormSchema, DepartureReminderFormValues } from '../model/departureReminderForm.schema'

interface Props {
  show: boolean
  setShow: (show: boolean) => void
  taskId: number
  plannedDepartureDuration: number
  onSuccess: () => void
}

export function DepartureReminderModal({
  show,
  setShow,
  taskId,
  plannedDepartureDuration,
  onSuccess,
}: Props) {
  const queryClient = useQueryClient()

  const saveAbfahrt = useMutation({
    mutationFn: (dto: DepartureReminderFormValues) =>
      taskApi.update(taskId, { actualDepartureDuration: dto.actualDepartureDuration }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) })
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() })
      queryClient.invalidateQueries({ queryKey: taskKeys.all })
    },
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<DepartureReminderFormValues>({
    resolver: zodResolver(departureReminderFormSchema),
    defaultValues: {
      actualDepartureDuration: plannedDepartureDuration,
    },
  })

  useEffect(() => {
    if (show) {
      reset({ actualDepartureDuration: plannedDepartureDuration })
    }
  }, [show, plannedDepartureDuration, reset])

  const onSubmit = (values: DepartureReminderFormValues) => {
    saveAbfahrt.mutate(values, { onSuccess })
  }

  return (
    <Modal
      show={show}
      setShow={setShow}
      title="Abfahrt nachtragen"
      submitButton={{ onSubmit: handleSubmit(onSubmit), title: 'Speichern', isPending: saveAbfahrt.isPending }}
    >
      <div className="flex flex-col gap-4 w-full">
        <Controller
          name="actualDepartureDuration"
          control={control}
          render={({ field }) => (
            <DurationSpinner
              label="Abfahrt (min)"
              value={field.value}
              onChange={field.onChange}
              error={errors.actualDepartureDuration?.message}
            />
          )}
        />
      </div>
    </Modal>
  )
}
